import { useMemo } from "react";
import {
  ClientSideRowModelModule,
  DateFilterModule,
  ModuleRegistry,
  NumberFilterModule,
  PinnedRowModule,
  RowSelectionModule,
  TextFilterModule,
  type CellStyle,
  type ColDef
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

export type QueryColumnMeta = {
  name: string;
  type: string; // Normalized domain: "string" | "number" | "date" | "boolean"
  rawType?: string; // Raw SQL type from connector (e.g., "SQL_VARCHAR", "SQL_BIGINT")
};

export type QueryResultsPayload = {
  columns: QueryColumnMeta[];
  rows: unknown[][];
  durationMs: number;
  rowCount: number;
  /** ADR-0119: the connector stopped fetching at the user's Limit (source total unknown). */
  truncated?: boolean;
};

export type GridDisplayOptions = {
  rowTotals?: boolean;
  columnTotals?: boolean;
};

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowSelectionModule,
  PinnedRowModule
]);

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

type AiColDef = {
  field?: string;
  headerName?: string;
  aggFunc?: string;
  sortable?: boolean;
  filter?: boolean | string;
  resizable?: boolean;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  pinned?: "left" | "right" | boolean;
  sort?: "asc" | "desc";
  hide?: boolean;
  type?: string | string[];
  valueFormatter?: string;
  cellStyle?: Record<string, unknown>;
  [key: string]: unknown;
};

function buildColumnDefsFromAiConfig(
  aiColumnDefs: AiColDef[],
  resultColumns: QueryColumnMeta[]
): ColDef[] {
  const colSet = new Set(resultColumns.map((c) => c.name));
  return aiColumnDefs
    .filter((def) => !def.field || colSet.has(def.field))
    .map((def) => {
      const col: ColDef = {
        field: def.field,
        headerName: def.headerName ?? def.field,
        sortable: def.sortable !== false,
        filter: def.filter !== false,
        resizable: def.resizable !== false,
        tooltipField: def.field,
        valueFormatter: (params) => formatCellValue(params.value),
        cellStyle: (params) =>
          params.node.rowPinned === "bottom" ? { fontWeight: 600 } : null
      };
      if (def.width) col.width = def.width;
      if (def.minWidth) col.minWidth = def.minWidth;
      if (def.maxWidth) col.maxWidth = def.maxWidth;
      if (def.flex !== undefined) col.flex = def.flex;
      if (def.pinned) col.pinned = def.pinned;
      if (def.sort) col.sort = def.sort;
      if (def.hide) col.hide = def.hide;
      return col;
    });
}

function computeTotalsFromAggFunc(
  aiColumnDefs: AiColDef[],
  rowData: Array<Record<string, unknown>>
): Record<string, unknown> | null {
  const aggColumns = aiColumnDefs.filter((d) => d.field && d.aggFunc);
  if (aggColumns.length === 0) return null;

  const totalsRow: Record<string, unknown> = {};
  let labelSet = false;

  for (const def of aiColumnDefs) {
    if (!def.field) continue;
    const aggCol = aggColumns.find((a) => a.field === def.field);
    if (aggCol) {
      const values = rowData
        .map((r) => r[aggCol.field!])
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
      switch (aggCol.aggFunc) {
        case "sum":
          totalsRow[aggCol.field!] = values.reduce((a, b) => a + b, 0);
          break;
        case "avg":
          totalsRow[aggCol.field!] = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
          break;
        case "min":
          totalsRow[aggCol.field!] = values.length > 0 ? Math.min(...values) : null;
          break;
        case "max":
          totalsRow[aggCol.field!] = values.length > 0 ? Math.max(...values) : null;
          break;
        case "count":
          totalsRow[aggCol.field!] = values.length;
          break;
        default:
          totalsRow[aggCol.field!] = null;
      }
    } else if (!labelSet) {
      totalsRow[def.field] = "Total";
      labelSet = true;
    } else {
      totalsRow[def.field] = null;
    }
  }

  return totalsRow;
}

export function QueryResultsGrid(props: {
  result: QueryResultsPayload;
  hideMeta?: boolean;
  gridOptions?: GridDisplayOptions;
  gridConfig?: Record<string, unknown>;
}) {
  const { result, hideMeta, gridOptions, gridConfig } = props;
  const showRowTotals = gridOptions?.rowTotals === true;
  const showColumnTotals = gridOptions?.columnTotals === true;
  const aiColumnDefs = gridConfig?.columnDefs as AiColDef[] | undefined;
  const aiDefaultColDef = gridConfig?.defaultColDef as Record<string, unknown> | undefined;

  const numericColNames = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    result.columns.forEach((col) => {
      if (col.type === "number") s.add(col.name);
    });
    return s;
  }, [result.columns]);

  const { rowData, rowTotalColName } = useMemo(() => {
    const ROW_TOTAL_COL = showRowTotals ? "__row_total__" : null;

    const data = result.rows.map((row) => {
      const obj: Record<string, unknown> = {};
      let rowSum = 0;
      result.columns.forEach((col, idx) => {
        obj[col.name] = row[idx];
        if (showRowTotals && numericColNames.has(col.name)) {
          const v = row[idx];
          if (typeof v === "number" && !isNaN(v)) rowSum += v;
        }
      });
      if (ROW_TOTAL_COL) obj[ROW_TOTAL_COL] = rowSum;
      return obj;
    });

    return { rowData: data, rowTotalColName: ROW_TOTAL_COL };
  }, [result.columns, result.rows, numericColNames, showRowTotals]);

  const pinnedBottomRowData = useMemo<Record<string, unknown>[] | undefined>(() => {
    // If AI provided columnDefs with aggFunc, compute totals from those
    if (Array.isArray(aiColumnDefs) && aiColumnDefs.length > 0) {
      const totals = computeTotalsFromAggFunc(aiColumnDefs, rowData);
      if (totals) return [totals];
    }

    // Legacy: compute from gridOptions.columnTotals
    if (!showColumnTotals) return undefined;

    const totalsRow: Record<string, unknown> = {};
    result.columns.forEach((col, idx) => {
      if (numericColNames.has(col.name)) {
        let sum = 0;
        for (const row of result.rows) {
          const v = row[idx];
          if (typeof v === "number" && !isNaN(v)) sum += v;
        }
        totalsRow[col.name] = sum;
      } else {
        if (!totalsRow["__labelSet__"]) {
          totalsRow[col.name] = "Total";
          totalsRow["__labelSet__"] = true;
        } else {
          totalsRow[col.name] = null;
        }
      }
    });
    delete totalsRow["__labelSet__"];

    if (rowTotalColName) {
      let sum = 0;
      for (const row of rowData) {
        const v = row[rowTotalColName];
        if (typeof v === "number" && !isNaN(v)) sum += v;
      }
      totalsRow[rowTotalColName] = sum;
    }

    return [totalsRow];
  }, [aiColumnDefs, rowData, result.columns, result.rows, numericColNames, showColumnTotals, rowTotalColName]);

  const columnDefs = useMemo<ColDef[]>(() => {
    // If AI provided columnDefs, use them
    if (Array.isArray(aiColumnDefs) && aiColumnDefs.length > 0) {
      const defs = buildColumnDefsFromAiConfig(aiColumnDefs, result.columns);
      if (defs.length > 0) return defs;
    }

    // Fallback: auto-generate from result columns
    const defs: ColDef[] = result.columns.map((column) => ({
      field: column.name,
      headerName: column.name,
      sortable: true,
      filter: true,
      resizable: true,
      tooltipField: column.name,
      valueFormatter: (params) => formatCellValue(params.value),
      cellStyle: (params) =>
        params.node.rowPinned === "bottom" ? { fontWeight: 600 } : null
    }));

    if (rowTotalColName) {
      defs.push({
        field: rowTotalColName,
        headerName: "Total",
        sortable: false,
        filter: false,
        resizable: true,
        minWidth: 120,
        valueFormatter: (params) => formatCellValue(params.value),
        cellStyle: (params): CellStyle => {
          const style: CellStyle = { fontWeight: 600 };
          if (params.node.rowPinned !== "bottom") {
            style.color = "var(--muted)";
          }
          return style;
        }
      });
    }

    return defs;
  }, [aiColumnDefs, result.columns, rowTotalColName]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 130,
    flex: 1,
    ...(aiDefaultColDef ?? {})
  }), [aiDefaultColDef]);

  return (
    <div className="queryResultsSection">
      {!hideMeta ? (
        <div className="queryMeta">
          <span className="pill">Duration: {result.durationMs} ms</span>
          <span className="pill">Rows: {result.rowCount.toLocaleString()}</span>
        </div>
      ) : null}

      {result.rowCount === 0 ? (
        <div className="muted">Query returned no rows.</div>
      ) : null}

      {columnDefs.length === 0 && result.rowCount > 0 ? <div className="muted">Rows returned but no columns were provided.</div> : null}

      <div className="ag-theme-quartz queryGridHost">
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pinnedBottomRowData={pinnedBottomRowData}
          animateRows
          rowSelection={{ mode: "singleRow" }}
        />
      </div>
    </div>
  );
}
